/**
 * Slug → route metadata registry.
 *
 * Maps the staff feature catalogue's `slug` field to a BI route. The
 * Stage 2 sidebar uses this metadata to filter which entries to show
 * based on the catalogue, and a future iteration (Stage 4+) will use
 * `Component` factories to mount unknown slugs against a generic table
 * view.
 *
 * NOTE: the lazy `Component` factories were intentionally removed in
 * favour of static imports inside `router.tsx` — Vite warned about
 * "dynamically imported but also statically imported" because both
 * code paths competed for the same module, and the static one won.
 * Re-introduce a lazy variant once we actually mount routes from this
 * registry instead of from `router.tsx`.
 */
export interface RouteShape {
  /** Path mounted on the protected shell. */
  path: string;
  /** Page-level scope used by ErrorBoundary + telemetry. */
  scope: string;
  /** Lucide icon name (matches the staff `icon` field where possible). */
  icon?: string;
  /** Default sidebar group (used as fallback when feature map is unavailable). */
  group?:
    | 'dashboards'
    | 'workspace'
    | 'people'
    | 'supply'
    | 'merchandising'
    | 'growth'
    | 'admin'
    | 'crm';
  /** Pretty label (falls back to the feature's `name` from the catalogue). */
  label?: string;
  /** Optional parent resource for nested pages and report widgets. */
  parent?: string;
}

/**
 * The canonical map. Slugs in the LHS column come from the staff feature
 * catalogue (`Feature.slug`). Where BI's URL differs from the slug we
 * keep the slug authoritative for permission checks but mount whatever
 * `path` we've shipped historically.
 */
export const FEATURE_REGISTRY: Record<string, RouteShape> = {
  overview: { path: '/', scope: 'overview', icon: 'gauge', group: 'dashboards', label: 'Overview' },
  'live-ops': { path: '/live', scope: 'live-ops', icon: 'activity', group: 'dashboards', label: 'Live Ops' },
  orders: { path: '/orders', scope: 'orders', icon: 'package-check', group: 'dashboards', label: 'Orders' },
  'orders.sla-heatmap': { path: '/orders/sla-heatmap', scope: 'orders-sla-heatmap', icon: 'package-check', group: 'dashboards', label: 'SLA Heatmap', parent: 'orders' },
  'orders.durations': { path: '/orders/durations', scope: 'orders-durations', icon: 'package-check', group: 'dashboards', label: 'Durations', parent: 'orders' },
  'orders.detail': { path: '/orders/:id', scope: 'order-detail', icon: 'package-check', group: 'dashboards', label: 'Order Detail', parent: 'orders' },
  customers: { path: '/customers', scope: 'customers', icon: 'users', group: 'people', label: 'Customers' },
  'customers.detail': { path: '/customers/:id', scope: 'customer-detail', icon: 'users', group: 'people', label: 'Customer Detail', parent: 'customers' },
  stores: { path: '/stores', scope: 'stores', icon: 'users', group: 'people', label: 'Store Health' },
  riders: { path: '/riders', scope: 'riders', icon: 'users', group: 'people', label: 'Rider Network' },
  'riders.distribution': { path: '/riders/distribution', scope: 'riders-distribution', icon: 'users', group: 'people', label: 'Rider Distribution', parent: 'riders' },
  network: { path: '/network', scope: 'network', icon: 'users', group: 'people', label: 'Network' },
  'network.darkstore-siting': { path: '/network/darkstore-siting', scope: 'network-darkstore-siting', icon: 'users', group: 'people', label: 'Darkstore Siting', parent: 'network' },
  growth: { path: '/growth', scope: 'growth', icon: 'trending-up', group: 'growth', label: 'Growth' },
  'growth.rfm': { path: '/growth/rfm', scope: 'growth-rfm', icon: 'trending-up', group: 'growth', label: 'RFM', parent: 'growth' },
  'growth.abandonment': { path: '/growth/abandonment', scope: 'growth-abandonment', icon: 'trending-up', group: 'growth', label: 'Cart Abandonment', parent: 'growth' },
  'growth.coupons-referrals': { path: '/growth/coupons-referrals', scope: 'growth-coupons-referrals', icon: 'trending-up', group: 'growth', label: 'Coupons & Referrals', parent: 'growth' },
  'growth.coupons': { path: '/growth/coupons', scope: 'growth-coupons', icon: 'trending-up', group: 'growth', label: 'Coupons', parent: 'growth' },
  'growth.referrals': { path: '/growth/referrals', scope: 'growth-referrals', icon: 'trending-up', group: 'growth', label: 'Referrals', parent: 'growth' },
  search: { path: '/search', scope: 'search', icon: 'trending-up', group: 'growth', label: 'Search' },
  wishlist: { path: '/wishlist', scope: 'wishlist', icon: 'trending-up', group: 'growth', label: 'Wishlist' },
  waitlist: { path: '/waitlist', scope: 'waitlist', icon: 'trending-up', group: 'growth', label: 'Waitlist' },
  supply: { path: '/supply', scope: 'supply', icon: 'boxes', group: 'supply', label: 'Supply' },
  inventory: { path: '/inventory', scope: 'inventory', icon: 'layers', group: 'supply', label: 'Stock Ledger' },
  shrinkage: { path: '/shrinkage', scope: 'shrinkage', icon: 'shield-alert', group: 'supply', label: 'Shrinkage' },
  'inventory.forecast': { path: '/inventory/forecast', scope: 'forecast', icon: 'layers', group: 'supply', label: 'Inventory Forecast', parent: 'inventory' },
  'inventory.replenishment': { path: '/inventory/replenishment', scope: 'replenishment', icon: 'layers', group: 'supply', label: 'Replenishment', parent: 'inventory' },
  vendors: { path: '/vendors', scope: 'vendors', icon: 'bar-chart-3', group: 'supply', label: 'Vendors' },
  'vendors.profitability': { path: '/vendors/profitability', scope: 'vendors-profitability', icon: 'bar-chart-3', group: 'supply', label: 'Profitability', parent: 'vendors' },
  procurement: { path: '/procurement', scope: 'procurement', icon: 'boxes', group: 'supply', label: 'Procurement' },
  products: { path: '/products', scope: 'products', icon: 'package', group: 'merchandising', label: 'Products' },
  'products.brands': { path: '/products/brands', scope: 'products-brands', icon: 'package', group: 'merchandising', label: 'Brands', parent: 'products' },
  'products.categories': { path: '/products/categories', scope: 'products-categories', icon: 'package', group: 'merchandising', label: 'Categories', parent: 'products' },
  'products.forecast': { path: '/products/forecast', scope: 'products-forecast', icon: 'trending-up', group: 'merchandising', label: 'Demand Forecast', parent: 'products' },
  'products.map': { path: '/products/map', scope: 'products-map', icon: 'map', group: 'merchandising', label: 'Sales Map', parent: 'products' },
  'products.map3d': { path: '/products/map3d', scope: 'products-map3d', icon: 'map', group: 'merchandising', label: '3D Building Map', parent: 'products' },
  'products.detail': { path: '/products/:id', scope: 'products-detail', icon: 'package', group: 'merchandising', label: 'Product Detail', parent: 'products' },
  strategic: { path: '/strategic', scope: 'strategic', icon: 'bar-chart-3', group: 'dashboards', label: 'Strategic' },
  reports: { path: '/reports', scope: 'reports', icon: 'file-text', group: 'workspace', label: 'Reports' },
  roster: { path: '/roster', scope: 'roster', icon: 'settings', group: 'workspace', label: 'Roster' },
  payments: { path: '/payments', scope: 'payments', icon: 'settings', group: 'workspace', label: 'Payments' },
  'payments.refunds': { path: '/payments/refunds', scope: 'payments-refunds', icon: 'settings', group: 'workspace', label: 'Refund Settlement', parent: 'payments' },
  'inventory.audits': { path: '/inventory/audits', scope: 'inventory-audits', icon: 'layers', group: 'supply', label: 'Stock Audits', parent: 'inventory' },
  'inventory.transfers': { path: '/inventory/transfers', scope: 'inventory-transfers', icon: 'layers', group: 'supply', label: 'Transfers', parent: 'inventory' },
  'inventory.lost-items': { path: '/inventory/lost-items', scope: 'inventory-lost-items', icon: 'layers', group: 'supply', label: 'Lost Items', parent: 'inventory' },
  feedback: { path: '/feedback', scope: 'feedback', icon: 'settings', group: 'workspace', label: 'Feedback' },
  notifications: { path: '/notifications', scope: 'notifications', icon: 'settings', group: 'workspace', label: 'Notifications' },
  alerts: { path: '/alerts', scope: 'alerts', icon: 'settings', group: 'workspace', label: 'Alerts' },
  audit: { path: '/settings/audit', scope: 'audit', icon: 'history', group: 'admin', label: 'Audit Log', parent: 'settings' },
  wall: { path: '/wall', scope: 'wall', icon: 'activity', group: 'dashboards', label: 'Wall' },
  settings: { path: '/settings', scope: 'settings', icon: 'settings', group: 'admin', label: 'Settings' },
  'settings.access-control': { path: '/settings/access', scope: 'settings-access', icon: 'shield-check', group: 'admin', label: 'Access Control', parent: 'settings' },
  'settings.user-management': { path: '/settings/users', scope: 'user-management', icon: 'users', group: 'admin', label: 'Users', parent: 'settings' },
  'settings.user-management.detail': { path: '/settings/users/:id', scope: 'user-management-detail', icon: 'users', group: 'admin', label: 'User Detail', parent: 'settings.user-management' },
  crm: { path: '/crm', scope: 'crm', icon: 'life-buoy', group: 'crm', label: 'Support CRM' },
  'crm.inbox': { path: '/crm/inbox', scope: 'crm-inbox', icon: 'inbox', group: 'crm', label: 'Inbox', parent: 'crm' },
  'crm.conversations': { path: '/crm/conversations', scope: 'crm-conversations', icon: 'messages-square', group: 'crm', label: 'Conversations', parent: 'crm' },
  'crm.conversations.detail': { path: '/crm/conversations/:id', scope: 'crm-conversation-detail', icon: 'messages-square', group: 'crm', label: 'Conversation', parent: 'crm.conversations' },
  'crm.tickets': { path: '/crm/tickets', scope: 'crm-tickets', icon: 'ticket', group: 'crm', label: 'Tickets', parent: 'crm' },
  'crm.tickets.detail': { path: '/crm/tickets/:id', scope: 'crm-ticket-detail', icon: 'ticket', group: 'crm', label: 'Ticket Detail', parent: 'crm.tickets' },
  'crm.refunds': { path: '/crm/refunds', scope: 'crm-refunds', icon: 'receipt', group: 'crm', label: 'Refunds', parent: 'crm' },
  'crm.shrinkage': { path: '/crm/shrinkage', scope: 'crm-shrinkage', icon: 'shield-alert', group: 'crm', label: 'Shrinkage', parent: 'crm' },
  // CRM configuration lives under the settings hub — the slugs are unchanged
  // (so no grant has to be re-issued) but the paths moved out of `/crm`.
  'crm.settings': { path: '/settings/support', scope: 'crm-settings', icon: 'headset', group: 'crm', label: 'Support CRM', parent: 'settings' },
  'crm.settings.routing': { path: '/settings/support/routing', scope: 'crm-settings-routing', icon: 'shield-alert', group: 'crm', label: 'Routing Matrix', parent: 'crm.settings' },
  'crm.settings.email': { path: '/settings/support/email', scope: 'crm-settings-email', icon: 'settings', group: 'crm', label: 'Email (SMTP)', parent: 'crm.settings' },
  'crm.settings.whatsapp': { path: '/settings/support/whatsapp', scope: 'crm-settings-whatsapp', icon: 'message-circle', group: 'crm', label: 'WhatsApp', parent: 'crm.settings' },
  'crm.settings.meta': { path: '/settings/support/meta', scope: 'crm-settings-meta', icon: 'share-2', group: 'crm', label: 'Meta Channels', parent: 'crm.settings' },
  'crm.settings.ai': { path: '/settings/support/ai', scope: 'crm-settings-ai', icon: 'bot', group: 'crm', label: 'AI Agent', parent: 'crm.settings' },
  'crm.settings.templates': { path: '/settings/support/templates', scope: 'crm-settings-templates', icon: 'settings', group: 'crm', label: 'Notification Templates', parent: 'crm.settings' },
  'crm.settings.cancellations': { path: '/settings/support/cancellations', scope: 'crm-settings-cancellations', icon: 'settings', group: 'crm', label: 'Cancellation Sources', parent: 'crm.settings' },
  'crm.settings.sla': { path: '/settings/support/sla', scope: 'crm-settings-sla', icon: 'gauge', group: 'crm', label: 'SLA & Breach Policy', parent: 'crm.settings' },
  'crm.settings.reason_codes': {
    path: '/settings/support/reason-codes',
    scope: 'crm-settings-reason-codes',
    icon: 'layers',
    group: 'crm',
    label: 'Reason codes',
    parent: 'crm.settings',
  },
  'crm.analytics.sla': { path: '/crm/analytics/sla', scope: 'crm-analytics-sla', icon: 'bar-chart-3', group: 'crm', label: 'SLA Disposition Analytics', parent: 'crm' },
  'crm.settings.canned_replies': { path: '/settings/support/canned-replies', scope: 'crm-settings-canned-replies', icon: 'zap', group: 'crm', label: 'Canned Replies', parent: 'crm.settings' },
  'crm.settings.engagement': { path: '/settings/support/engagement', scope: 'crm-settings-engagement', icon: 'megaphone', group: 'crm', label: 'Engagement & Operations', parent: 'crm.settings' },
  'crm.analytics.conversations': { path: '/crm/analytics/conversations', scope: 'crm-analytics-conversations', icon: 'bar-chart-3', group: 'crm', label: 'Support Analytics', parent: 'crm' },
};

/** Path → slug, computed once. Useful for current-route inference. */
export const PATH_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(FEATURE_REGISTRY).map(([slug, r]) => [r.path, slug]),
);

/** Controls the Topbar exposes. */
export type FilterKey = 'date' | 'timeMode' | 'store' | 'compare';

/** Everything an analytics page normally reads. */
const ALL_FILTERS: FilterKey[] = ['date', 'timeMode', 'store', 'compare'];

/**
 * Which global filters each route actually consumes.
 *
 * The Topbar used to render the whole rail on every screen, which was actively
 * misleading: Settings, Users and the CRM config pages read none of it, and
 * Payments deliberately ignores the store filter while still showing a store
 * picker — so an operator could pick a store, see the numbers not move, and
 * reasonably conclude the page was broken.
 *
 * Derived from what each page reads out of `useFilters`. Anything absent falls
 * back to the full rail, so a new page keeps today's behaviour until someone
 * declares otherwise.
 */
export const ROUTE_FILTERS: Record<string, FilterKey[]> = {
  overview: ALL_FILTERS,
  strategic: ALL_FILTERS,
  growth: ALL_FILTERS,
  'live-ops': ['date', 'timeMode', 'store'],

  orders: ['date', 'timeMode', 'store'],
  'orders.sla-heatmap': ['date', 'timeMode', 'store'],
  'orders.durations': ['date', 'timeMode', 'store'],
  'orders.detail': [],
  roster: ['date', 'timeMode', 'store'],
  riders: [],
  'riders.distribution': ['date', 'timeMode', 'store'],

  stores: [],
  network: ['date', 'timeMode', 'store'],
  'network.darkstore-siting': ['date', 'timeMode', 'store'],

  supply: ['date', 'timeMode', 'store'],
  inventory: ['date', 'timeMode', 'store'],
  'inventory.replenishment': ['date', 'timeMode', 'store'],
  // Forecasts are cumulative, so a date range would mean nothing here.
  'inventory.forecast': ['store'],
  'inventory.lost-items': ['date', 'timeMode', 'store'],
  shrinkage: ['date', 'timeMode', 'store'],

  vendors: ['date', 'timeMode'],
  'vendors.profitability': ['date', 'timeMode', 'store'],
  procurement: ['date', 'timeMode'],

  // The products family shares `useProductScope`, which reads range, store and
  // compare regardless of which of the six screens is mounted.
  products: ALL_FILTERS,
  'products.brands': ALL_FILTERS,
  'products.categories': ALL_FILTERS,
  'products.forecast': ALL_FILTERS,
  'products.map': ALL_FILTERS,
  'products.map3d': ALL_FILTERS,
  'products.detail': ['date', 'timeMode', 'store'],

  customers: [],
  'customers.detail': [],
  'growth.rfm': ['date', 'timeMode', 'store'],
  'growth.abandonment': ['date', 'timeMode', 'store'],
  'growth.coupons-referrals': ['date', 'timeMode', 'store'],
  'growth.coupons': ['date', 'timeMode', 'store'],
  'growth.referrals': ['date', 'timeMode', 'store'],
  search: ['date', 'timeMode', 'store'],
  // Wishlist counts are a live total, not a windowed one.
  wishlist: ['store'],
  waitlist: ['date', 'timeMode'],
  feedback: ['date', 'timeMode'],

  reports: ['date', 'timeMode', 'store'],
  notifications: ['date', 'timeMode'],
  alerts: [],

  // Support CRM screens carry their own queue filters and ignore the rail.
  crm: [],
  'crm.inbox': [],
  'crm.conversations': [],
  'crm.conversations.detail': [],
  'crm.tickets': [],
  'crm.tickets.detail': [],
  'crm.refunds': [],
  'crm.shrinkage': [],
  'crm.analytics.sla': [],
  'crm.analytics.conversations': [],

  // Administration: nothing here is scoped by date or store.
  settings: [],
  'settings.access-control': [],
  'settings.user-management': [],
  'settings.user-management.detail': [],
  audit: [],
};

/**
 * Filters to show for a pathname. Unknown routes get the full rail rather than
 * none, so a page that reads the filters is never silently starved of them.
 */
export function filtersForPath(pathname: string): FilterKey[] {
  const slug = slugForPath(pathname);
  if (!slug) return ALL_FILTERS;
  return ROUTE_FILTERS[slug] ?? ALL_FILTERS;
}

/**
 * Best-effort slug for a pathname, matching parameterised routes too.
 *
 * Exact hits win; otherwise the longest registry path that prefixes the URL
 * does, which resolves `/orders/123` to `orders.detail` and
 * `/settings/support/routing` to its own entry rather than to `settings`.
 */
export function slugForPath(pathname: string): string | null {
  const exact = PATH_TO_SLUG[pathname];
  if (exact) return exact;

  let best: { slug: string; length: number } | null = null;
  for (const [slug, route] of Object.entries(FEATURE_REGISTRY)) {
    const pattern = route.path;
    if (pattern.includes(':')) {
      if (matchesPattern(pattern, pathname) && (!best || pattern.length > best.length)) {
        best = { slug, length: pattern.length };
      }
      continue;
    }
    if (pattern !== '/' && pathname.startsWith(`${pattern}/`) && (!best || pattern.length > best.length)) {
      best = { slug, length: pattern.length };
    }
  }
  return best?.slug ?? null;
}

function matchesPattern(pattern: string, pathname: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, i) => part.startsWith(':') || part === pathParts[i]);
}

/**
 * Ancestor trail for a route, from the registry `parent` chain.
 *
 * Deep pages had no way back up — drilling from Users into a user, or into a
 * CRM config page, left the browser Back button as the only exit.
 */
export function breadcrumbsForPath(pathname: string): { label: string; path: string }[] {
  const slug = slugForPath(pathname);
  if (!slug) return [];

  const trail: { label: string; path: string }[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined = slug;

  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const route: RouteShape | undefined = FEATURE_REGISTRY[cursor];
    if (!route) break;
    trail.unshift({ label: route.label ?? cursor, path: route.path });
    cursor = route.parent;
  }

  return trail;
}

/** Slugs we always show in the sidebar even if the feature map omits them. */
export const ALWAYS_VISIBLE_SLUGS: ReadonlySet<string> = new Set([
  'settings',
]);
